import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  LayoutGrid,
  List,
  Plus,
  Search,
  StickyNote,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, dataBR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, ErrorState, Loading, PageHeader, Surface } from "@/components/ds";
import { NoteModal } from "@/components/notes/NoteModal";
import { NoteDetailModal } from "@/components/notes/NoteDetailModal";
import { COLUMNS, NOTE_COLORS, dueTone, matchesFilter, noteIsLate } from "@/components/notes/noteUtils";

const QUICK_FILTERS = [
  { id: "ABERTOS", label: "Abertos" },
  { id: "MEUS", label: "Meus recados" },
  { id: "ATRIBUIDOS", label: "Atribuídos a mim" },
  { id: "ATRASADOS", label: "Atrasados" },
  { id: "URGENTES", label: "Urgentes" },
  { id: "CONCLUIDOS", label: "Concluídos" },
  { id: "TODOS", label: "Todos" },
];

const PostIt = ({ note, today, onOpen }) => {
  const palette = NOTE_COLORS[note.color] || NOTE_COLORS.AMARELO;
  const tone = dueTone(note, today);
  return (
    <button
      onClick={() => onOpen(note)}
      data-testid={`note-card-${note.id}`}
      className={`group w-full rounded-xl border p-4 text-left shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand/50 ${palette.card}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className={`font-display text-sm font-semibold leading-snug ${palette.text}`}>
          {note.title}
        </p>
        {note.priority === "URGENTE" && note.status !== "CONCLUIDO" && (
          <span
            className="shrink-0 rounded-full bg-red-600/90 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white"
            data-testid={`note-urgent-${note.id}`}
          >
            Urgente
          </span>
        )}
      </div>
      <p className={`mt-1.5 line-clamp-2 text-xs leading-snug ${palette.soft}`}>
        {note.description}
      </p>
      {note.reference && (
        <p className={`mt-2 truncate text-[11px] font-medium ${palette.soft}`}>
          {note.reference}
        </p>
      )}
      <div className={`mt-3 flex items-center justify-between gap-2 text-[11px] ${palette.soft}`}>
        <span className="flex items-center gap-1 truncate">
          <User className="h-3 w-3 shrink-0" /> {note.assignee}
        </span>
        <span
          className={`flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 font-medium ${tone.badge}`}
          data-testid={`note-due-${note.id}`}
        >
          <CalendarClock className="h-3 w-3" /> {dataBR(note.due_date)}
        </span>
      </div>
      <p className={`mt-2 text-[10px] uppercase tracking-wide ${palette.soft}`}>
        {note.status === "CONCLUIDO"
          ? note.outcome === "RESOLVIDO"
            ? "Concluído · resolvido"
            : "Concluído · não resolvido"
          : note.status === "EM_ANDAMENTO"
          ? "Em andamento"
          : "Novo"}
      </p>
    </button>
  );
};

export default function Notes() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [view, setView] = useState("BOARD");
  const [filter, setFilter] = useState("ABERTOS");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [opened, setOpened] = useState(null);

  const load = async () => {
    setError("");
    try {
      const { data: res } = await api.get("/notes");
      setData(res);
      setOpened((prev) => (prev ? res.notes.find((n) => n.id === prev.id) || null : null));
    } catch (e) {
      setError(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const notes = data?.notes || [];
  const today = data?.today || new Date().toISOString().slice(0, 10);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return notes.filter((n) => {
      if (!matchesFilter(n, filter, today, user)) return false;
      if (!term) return true;
      return [n.title, n.description, n.reference, n.assignee, n.notes]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(term));
    });
  }, [notes, filter, search, today, user]);

  const grouped = useMemo(() => {
    const result = { NOVOS: [], EM_ANDAMENTO: [], ATRASADOS: [], CONCLUIDOS: [] };
    visible.forEach((n) => {
      if (n.status === "CONCLUIDO") result.CONCLUIDOS.push(n);
      else if (noteIsLate(n, today)) result.ATRASADOS.push(n);
      else if (n.status === "EM_ANDAMENTO") result.EM_ANDAMENTO.push(n);
      else result.NOVOS.push(n);
    });
    return result;
  }, [visible, today]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading label="Abrindo o mural..." />;

  const openCount = notes.filter((n) => n.status !== "CONCLUIDO").length;
  const lateCount = notes.filter((n) => noteIsLate(n, today)).length;

  return (
    <div className="space-y-6" data-testid="notes-page">
      <PageHeader
        title="Mural de Recados"
        subtitle={
          openCount === 0
            ? "Nenhuma pendência no mural. Tudo em dia por aqui."
            : `${openCount} recado${openCount > 1 ? "s" : ""} em aberto${
                lateCount ? ` · ${lateCount} com prazo vencido` : ""
              }.`
        }
        action={
          <Button
            onClick={() => setCreating(true)}
            data-testid="new-note-btn"
            className="h-11 gap-2 rounded-full px-5 font-semibold transition-transform active:scale-95"
          >
            <Plus className="h-4 w-4" /> Novo recado
          </Button>
        }
      />

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {QUICK_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`note-filter-${f.id.toLowerCase()}`}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                filter === f.id
                  ? "border-brand/50 bg-brand/15 text-brand"
                  : "border-white/10 text-muted-foreground hover:border-brand/30 hover:text-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por título, mensagem, cliente ou pedido"
              data-testid="note-search-input"
              className="pl-9"
            />
          </div>
          <div className="flex shrink-0 rounded-full border border-white/10 p-1">
            {[
              { id: "BOARD", label: "Quadro", icon: LayoutGrid },
              { id: "LIST", label: "Lista", icon: List },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setView(id)}
                data-testid={`note-view-${id.toLowerCase()}`}
                className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  view === id ? "bg-brand/15 text-brand" : "text-muted-foreground"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon={StickyNote}
          title="Nenhum recado por aqui"
          description="Cadastre um recado para não esquecer de retornar um orçamento, uma mensagem ou uma pendência."
          action={
            <Button onClick={() => setCreating(true)} className="rounded-full" data-testid="empty-new-note-btn">
              <Plus className="mr-2 h-4 w-4" /> Novo recado
            </Button>
          }
        />
      ) : view === "BOARD" ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 [&>*]:min-w-0" data-testid="notes-board">
          {COLUMNS.map((col) => (
            <div key={col.id} className="space-y-3" data-testid={`note-column-${col.id.toLowerCase()}`}>
              <div className="flex items-center gap-2 px-1">
                <span className={`h-2 w-2 rounded-full ${col.dot}`} />
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {col.label}
                </p>
                <span className="font-num text-xs text-muted-foreground/70">
                  {grouped[col.id].length}
                </span>
              </div>
              {grouped[col.id].length === 0 ? (
                <p className="rounded-xl border border-dashed border-white/10 px-3 py-6 text-center text-[11px] text-muted-foreground/60">
                  Nada aqui
                </p>
              ) : (
                grouped[col.id].map((n) => (
                  <PostIt key={n.id} note={n} today={today} onOpen={setOpened} />
                ))
              )}
            </div>
          ))}
        </div>
      ) : (
        <Surface className="overflow-x-auto p-0" data-testid="notes-list">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Recado</th>
                <th className="px-4 py-3 font-medium">Cliente / pedido</th>
                <th className="px-4 py-3 font-medium">Responsável</th>
                <th className="px-4 py-3 font-medium">Prazo</th>
                <th className="px-4 py-3 font-medium">Situação</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((n) => {
                const tone = dueTone(n, today);
                return (
                  <tr
                    key={n.id}
                    onClick={() => setOpened(n)}
                    data-testid={`note-row-${n.id}`}
                    className="cursor-pointer border-b border-white/5 transition-colors last:border-0 hover:bg-secondary/40"
                  >
                    <td className="max-w-[240px] px-4 py-3">
                      <p className="flex items-center gap-1.5 truncate font-medium">
                        {n.priority === "URGENTE" && n.status !== "CONCLUIDO" && (
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-400" />
                        )}
                        {n.title}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{n.description}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{n.reference || "-"}</td>
                    <td className="px-4 py-3 text-xs">{n.assignee}</td>
                    <td className={`whitespace-nowrap px-4 py-3 text-xs ${tone.text}`}>
                      {dataBR(n.due_date)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs">
                      {n.status === "CONCLUIDO" ? (
                        <span className="flex items-center gap-1 text-emerald-300">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {n.outcome === "RESOLVIDO" ? "Resolvido" : "Não resolvido"}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          {n.status === "EM_ANDAMENTO" ? "Em andamento" : "Novo"}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Surface>
      )}

      {(creating || editing) && (
        <NoteModal
          note={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={load}
        />
      )}
      {opened && (
        <NoteDetailModal
          note={opened}
          today={today}
          onClose={() => setOpened(null)}
          onEdit={(n) => {
            setOpened(null);
            setEditing(n);
          }}
          onChanged={load}
        />
      )}
    </div>
  );
}
